import type {
  PeopleTravelEvent,
  PeopleTravelEventKind,
  PeopleTravelLeg,
  PeopleTravelLegMode,
  PeopleTravelLocation,
} from '../types';
import { LOCATION_ORIGIN_X, LOCATION_ORIGIN_Y, LOCATION_X_GAP } from './peopleTravelCanvasGraph';

export type CanvasApplyDraft = {
  title?: string;
  purpose?: string;
  startDate?: string;
  endDate?: string;
  destinations?: unknown;
  summary?: string;
  locations?: unknown;
  events?: unknown;
  legs?: unknown;
  proposedDays?: unknown;
  travelerUserIds?: string[];
};

function coerceDraftList(raw: unknown): unknown[] {
  if (Array.isArray(raw)) return raw;
  if (raw == null) return [];
  if (typeof raw === 'object') return [raw];
  if (typeof raw !== 'string') return [];
  const s = raw.trim();
  if (!s) return [];
  const tryParse = (text: string): unknown[] | null => {
    try {
      const parsed = JSON.parse(text);
      if (Array.isArray(parsed)) return parsed;
      if (parsed && typeof parsed === 'object' && (parsed as { label?: string }).label) {
        return [parsed];
      }
    } catch {
      return null;
    }
    return null;
  };
  const parsed = tryParse(s) || tryParse(s.replace(/(-?\d+\.?\d*)[A-Za-z_]+\b/g, '$1'));
  if (parsed) return parsed;
  const labeled: Array<{ label: string }> = [];
  const labelRe = /"(?:label|name)"\s*:\s*"([^"]+)"/g;
  let labelMatch: RegExpExecArray | null = labelRe.exec(s);
  while (labelMatch) {
    labeled.push({ label: labelMatch[1] });
    labelMatch = labelRe.exec(s);
  }
  if (labeled.length) return labeled;
  if (/^[\[{]/.test(s)) return [];
  return [{ label: s }];
}

const EVENT_KINDS: PeopleTravelEventKind[] = ['work', 'meeting', 'hotel', 'meal', 'other'];
const LEG_MODES: PeopleTravelLegMode[] = ['flight', 'drive', 'rental_car', 'other'];
const TRANSPORT_KINDS = new Set(['flight', 'drive', 'rental_car', 'fly', 'car']);

export type CanvasExistingLocation = { id: string; label: string };

export type CanvasGraphWrites = {
  locations: PeopleTravelLocation[];
  events: PeopleTravelEvent[];
  legs: PeopleTravelLeg[];
};

type ProposedDayEvent = {
  title: string;
  kind: string;
  who?: string[];
  locationLabel?: string;
  dayLabel: string;
};

function slugId(prefix: string, label: string, index: number): string {
  const slug = label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 20);
  return `${prefix}_${index}_${slug || 'x'}`;
}

function isOriginLabel(label: string): boolean {
  const s = label.trim().toLowerCase();
  return s === 'home' || s === 'origin' || s === 'bna' || s.includes('airport');
}

function looksLikeReturn(title: string): boolean {
  return /\b(return|back home|fly home|drive home)\b/i.test(title);
}

function mentionsTransport(text: string): boolean {
  return /\b(fly|flight|flights|airport|drive|drove|driving|rental|rent a car)\b/i.test(text);
}

function inferLegMode(text: string): PeopleTravelLegMode {
  if (/\b(fly|flight|flights|airport)\b/i.test(text)) return 'flight';
  if (/\b(rental|rent a car)\b/i.test(text)) return 'rental_car';
  return 'drive';
}

function eventKindFrom(raw: string): PeopleTravelEventKind {
  const s = raw.toLowerCase();
  if (EVENT_KINDS.includes(s as PeopleTravelEventKind)) return s as PeopleTravelEventKind;
  if (s === 'lodge' || s === 'lodging' || s === 'stay') return 'hotel';
  if (s === 'activity' || s === 'site' || s === 'workday') return 'work';
  return 'other';
}

function parseProposedDayEvents(raw: unknown): ProposedDayEvent[] {
  const days = coerceDraftList(raw);
  const out: ProposedDayEvent[] = [];
  days.forEach(day => {
    if (!day || typeof day !== 'object') return;
    const row = day as Record<string, unknown>;
    const dayLabel = String(row.dayLabel || row.label || row.day || '').trim() || 'Day';
    coerceDraftList(row.events).forEach(ev => {
      if (!ev || typeof ev !== 'object') return;
      const item = ev as Record<string, unknown>;
      const title = String(item.title || item.label || '').trim();
      if (!title) return;
      out.push({
        title,
        kind: String(item.kind || 'other'),
        who: Array.isArray(item.who) ? item.who.map(v => String(v || '').trim()).filter(Boolean) : undefined,
        locationLabel: String(item.locationLabel || item.location || '').trim() || undefined,
        dayLabel,
      });
    });
  });
  return out;
}

function parseLocations(raw: unknown): Array<Partial<PeopleTravelLocation> & { label: string }> {
  const out: Array<Partial<PeopleTravelLocation> & { label: string }> = [];
  coerceDraftList(raw).forEach((item, index) => {
    if (typeof item === 'string' && item.trim()) {
      out.push({ label: item.trim(), sortOrder: index });
      return;
    }
    if (!item || typeof item !== 'object') return;
    const row = item as Record<string, unknown>;
    const label = String(row.label || row.name || row.title || '').trim();
    if (!label) return;
    out.push({
      id: typeof row.id === 'string' ? row.id : undefined,
      label,
      kind: typeof row.kind === 'string' ? row.kind : typeof row.type === 'string' ? row.type : undefined,
      address: typeof row.address === 'string' ? row.address : typeof row.place === 'string' ? row.place : undefined,
      placeId: typeof row.placeId === 'string' ? row.placeId : undefined,
      lat: typeof row.lat === 'number' ? row.lat : undefined,
      lng: typeof row.lng === 'number' ? row.lng : undefined,
      sortOrder: typeof row.sortOrder === 'number' ? row.sortOrder : index,
      x: typeof row.x === 'number' ? row.x : undefined,
      y: typeof row.y === 'number' ? row.y : undefined,
      startDate: typeof row.startDate === 'string' ? row.startDate : undefined,
      endDate: typeof row.endDate === 'string' ? row.endDate : undefined,
    });
  });
  return out;
}

function parseEvents(raw: unknown): Array<Partial<PeopleTravelEvent> & { title: string; locationLabel?: string }> {
  const out: Array<Partial<PeopleTravelEvent> & { title: string; locationLabel?: string }> = [];
  coerceDraftList(raw).forEach((item, index) => {
    if (!item || typeof item !== 'object') return;
    const row = item as Record<string, unknown>;
    const title = String(row.title || row.label || row.name || '').trim();
    if (!title) return;
    const locationRaw = row.locationId || row.location_id || row.locId;
    out.push({
      id: typeof row.id === 'string' ? row.id : undefined,
      locationId: typeof locationRaw === 'string' ? locationRaw : undefined,
      locationLabel: String(row.locationLabel || row.location || locationRaw || '').trim() || undefined,
      title,
      kind: eventKindFrom(String(row.kind || row.type || 'other')),
      startAt: typeof row.startAt === 'string' ? row.startAt : typeof row.start === 'string' ? row.start : undefined,
      endAt: typeof row.endAt === 'string' ? row.endAt : typeof row.end === 'string' ? row.end : undefined,
      notes: typeof row.notes === 'string' ? row.notes : undefined,
      x: typeof row.x === 'number' ? row.x : 16,
      y: typeof row.y === 'number' ? row.y : 44 + index * 88,
    });
  });
  return out;
}

function parseLegs(
  raw: unknown,
): Array<Partial<PeopleTravelLeg> & { fromLabel?: string; toLabel?: string; mode: PeopleTravelLegMode }> {
  const out: Array<Partial<PeopleTravelLeg> & { fromLabel?: string; toLabel?: string; mode: PeopleTravelLegMode }> = [];
  coerceDraftList(raw).forEach(item => {
    if (!item || typeof item !== 'object') return;
    const row = item as Record<string, unknown>;
    const modeRaw = String(row.mode || row.kind || row.type || 'other');
    const fromRaw = row.fromLocationId || row.from_location_id || row.fromId || row.from;
    const toRaw = row.toLocationId || row.to_location_id || row.toId || row.to;
    out.push({
      id: typeof row.id === 'string' ? row.id : undefined,
      fromLocationId: typeof fromRaw === 'string' ? fromRaw : undefined,
      toLocationId: typeof toRaw === 'string' ? toRaw : undefined,
      fromLabel: String(row.fromLabel || fromRaw || '').trim() || undefined,
      toLabel: String(row.toLabel || toRaw || '').trim() || undefined,
      mode: LEG_MODES.includes(modeRaw as PeopleTravelLegMode)
        ? (modeRaw as PeopleTravelLegMode)
        : inferLegMode(modeRaw),
      notes: typeof row.notes === 'string' ? row.notes : undefined,
      travelerUserIds: Array.isArray(row.travelerUserIds)
        ? row.travelerUserIds.map(uid => String(uid || '').trim()).filter(Boolean)
        : undefined,
      selectedOptionIds: Array.isArray(row.selectedOptionIds)
        ? row.selectedOptionIds.map(id => String(id || '').trim()).filter(Boolean)
        : undefined,
    });
  });
  return out;
}

function matchLocationId(
  locations: Array<{ id: string; label: string }>,
  id?: string,
  label?: string,
): string {
  if (id) {
    const exact = locations.find(l => l.id === id);
    if (exact) return exact.id;
  }
  const needle = String(label || '').trim().toLowerCase();
  if (!needle) return '';
  const exact = locations.find(l => l.label.trim().toLowerCase() === needle);
  if (exact) return exact.id;
  return (
    locations.find(l => {
      const loc = l.label.trim().toLowerCase();
      return loc.includes(needle) || needle.includes(loc);
    })?.id || ''
  );
}

/** Turn the worker's destinations / proposedDays draft into board cards. */
export function canvasWritesFromDraft(args: {
  existingLocations?: CanvasExistingLocation[];
  draft: CanvasApplyDraft;
}): CanvasGraphWrites {
  const existingLocations = args.existingLocations || [];
  const destinations = parseLocations(args.draft.destinations);
  let locations = parseLocations(args.draft.locations);
  let events = parseEvents(args.draft.events);
  let legs = parseLegs(args.draft.legs);
  const dayEvents = parseProposedDayEvents(args.draft.proposedDays);
  const blob = [args.draft.summary, ...dayEvents.map(e => `${e.kind} ${e.title}`)].join(' ');
  const hasTransport =
    dayEvents.some(e => TRANSPORT_KINDS.has(e.kind.toLowerCase()) || mentionsTransport(e.title)) ||
    mentionsTransport(args.draft.summary || '');

  if (!locations.length) {
    locations = destinations.map((dest, index) => ({
      label: dest.label.trim(),
      lat: dest.lat,
      lng: dest.lng,
      sortOrder: index,
    }));
  }

  const hasOrigin = locations.some(l => isOriginLabel(l.label));
  if (hasTransport && !hasOrigin && locations.length) {
    locations = [{ label: 'Home', sortOrder: 0 }, ...locations.map((loc, index) => ({ ...loc, sortOrder: index + 1 }))];
  }

  if (!events.length && !legs.length && dayEvents.length) {
    dayEvents.forEach(ev => {
      const kind = ev.kind.toLowerCase();
      const notes = [ev.dayLabel, ev.who?.join(', ')].filter(Boolean).join(' · ');
      const isTransport =
        TRANSPORT_KINDS.has(kind) || /\b(fly|flight|drive|drove)\b/i.test(ev.title);
      if (isTransport) {
        const origin = locations[0];
        const dest = locations.find(l => !isOriginLabel(l.label)) || locations[locations.length - 1];
        if (!origin || !dest || origin.label === dest.label) return;
        const returning = looksLikeReturn(ev.title);
        legs.push({
          fromLabel: returning ? dest.label : origin.label,
          toLabel: returning ? origin.label : dest.label,
          mode: kind === 'flight' || /fly|flight/i.test(ev.title) ? 'flight' : inferLegMode(ev.title || kind),
          notes,
        });
        return;
      }
      events.push({
        title: ev.title,
        kind: eventKindFrom(kind),
        locationLabel: ev.locationLabel,
        notes,
      });
    });
  }

  if (!legs.length && existingLocations.length === 0 && locations.length >= 2) {
    const mode = inferLegMode(blob);
    for (let i = 0; i < locations.length - 1; i += 1) {
      legs.push({
        fromLabel: locations[i].label,
        toLabel: locations[i + 1].label,
        mode,
      });
    }
  }

  const idMap = new Map<string, string>();
  existingLocations.forEach(loc => {
    idMap.set(loc.id, loc.id);
    idMap.set(loc.label.trim().toLowerCase(), loc.id);
  });

  const resolvedLocations: PeopleTravelLocation[] = locations.map((loc, index) => {
    const fromId = loc.id && idMap.get(loc.id);
    const fromLabel = idMap.get(loc.label.trim().toLowerCase());
    const id = fromId || fromLabel || loc.id || slugId('loc', loc.label, index);
    idMap.set(id, id);
    idMap.set(loc.label.trim().toLowerCase(), id);
    if (loc.id) idMap.set(loc.id, id);
    return {
      id,
      label: loc.label,
      kind: loc.kind,
      address: loc.address,
      placeId: loc.placeId,
      lat: loc.lat,
      lng: loc.lng,
      sortOrder: loc.sortOrder ?? index,
      x: loc.x ?? LOCATION_ORIGIN_X + index * LOCATION_X_GAP,
      y: loc.y ?? LOCATION_ORIGIN_Y,
      startDate: loc.startDate,
      endDate: loc.endDate,
    };
  });

  const resolvedEvents: PeopleTravelEvent[] = [];
  events.forEach((event, index) => {
    const locationId =
      matchLocationId(resolvedLocations, event.locationId, event.locationLabel) ||
      resolvedLocations.find(l => !isOriginLabel(l.label))?.id ||
      resolvedLocations[0]?.id;
    if (!locationId) return;
    resolvedEvents.push({
      id: event.id || slugId('event', event.title, index),
      locationId,
      title: event.title,
      kind: event.kind || 'other',
      startAt: event.startAt,
      endAt: event.endAt,
      notes: event.notes,
      x: event.x ?? 16,
      y: event.y ?? 44 + index * 88,
    });
  });

  const resolvedLegs: PeopleTravelLeg[] = [];
  legs.forEach((leg, index) => {
    const fromLocationId = matchLocationId(resolvedLocations, leg.fromLocationId, leg.fromLabel);
    const toLocationId = matchLocationId(resolvedLocations, leg.toLocationId, leg.toLabel);
    if (!fromLocationId || !toLocationId || fromLocationId === toLocationId) return;
    resolvedLegs.push({
      id: leg.id || slugId('leg', `${fromLocationId}-${toLocationId}`, index),
      fromLocationId,
      toLocationId,
      mode: leg.mode,
      travelerUserIds: leg.travelerUserIds,
      notes: leg.notes,
      selectedOptionIds: leg.selectedOptionIds,
    });
  });

  return { locations: resolvedLocations, events: resolvedEvents, legs: resolvedLegs };
}
